<template>
    <div>
        <template v-if="currentObject">
            <!-- Header -->
            <div v-if="mode === 'panel'" class="panel-header modeless">
                <h3 class="panel-header-title crop modeless">
                    {{ panelTitle }}
                </h3>
                <!-- Close icon -->
                <icon-close
                    :id="'information-detail-sidebar-close'"
                    @close="$_hide"
                    iconColor=""
                    class="d-inline-block float-right"
                >
                </icon-close>
            </div>
            <div class="panel-content">
                <Pagination
                    v-if="objects.length > 1"
                    :features="objects"
                    :indexStart="selectedIndex"
                    @indexChanged="$_onIndexChanged($event)"
                >
                </Pagination>
                <div class="object-content">
                    <object-detail
                        v-if="selectedObject"
                        :displayObject="selectedObject"
                        :extendedCellClass="extendedCellClass"
                    />
                </div>
                <!-- Buttons -->
                <div class="object-detail-buttons">
                    <!-- Print report -->
                    <base-button
                        v-if="isFeedBack && isMobile"
                        id="object_detail_print"
                        @click="$_printFeedback"
                        :text="$t('feedbackPrintTitle')"
                    />
                    <!-- FeedBack edit -->
                    <base-button
                        v-if="isFeedBack && isMobile && editable"
                        id="object_detail_edit"
                        @click="$_editFeedback"
                        :text="$t('labelButtonModify')"
                    />
                    <!-- FeedBack delete -->
                    <base-button
                        v-if="isFeedBack && remove"
                        id="object_detail_delete"
                        @click="$_deleteFeedbackModel"
                        :text="$t('btnDeleteLabel')"
                    />
                    <!-- Show map -->
                    <base-button
                        v-if="showToggleMapButton"
                        id="object_detail_show_map"
                        @click="display = !display"
                        variant="primary"
                        :text="$t('labelFeedbackShowMap')"
                    />
                </div>
            </div>
        </template>
    </div>
</template>

<script>
    import { mapGetters, mapState } from 'vuex';
    import { CONTENT_TYPE_ID } from '../../../../constants';
    import { isSmallDevice } from '../../../../services/utilities/devices';
    import IconClose from '../../../IconClose.vue';
    import Pagination from '../../../Pagination.vue';
    import ObjectDetail from '../../../geo/information/feature/ObjectDetail';
    import BaseButton from '../../../BaseComponents/base-button.vue';

    export default {
        components: {
            'icon-close': IconClose,
            Pagination: Pagination,
            'base-button': BaseButton,
            'object-detail': ObjectDetail
        },
        props: {
            objects: { type: Array, required: true },
            selectedObject: { type: Object, default: null },
            showToggleMapButton: { type: Boolean, default: false },
            active: { type: Boolean, default: true },
            mode: { type: String, default: 'panel' },
            extendedCellClass: { type: String, required: false }
        },
        data() {
            return { editable: false, remove: false };
        },
        watch: {
            selectedObject: {
                handler: function (newVal) {
                    if (this.active && newVal) {
                        // read selection in accessible mode in web
                        if (this.$store.getters.isWeb && this.mode === 'details') {
                            const text = newVal.info
                                .map(({ alias, displayValue }) => ({
                                    row: `${alias}: ${displayValue}`
                                }))
                                .map(({ row }) => row)
                                .join(', ');
                            this.$store.dispatch('say', { text });
                        }
                        if (newVal.type === 'api') {
                            this.$store.commit(
                                'SET_SELECTION_OBJECT_DATA_POINTER',
                                {
                                    group: newVal.groupIndex,
                                    index: newVal.itemIndex
                                }
                            );
                            this.$store.dispatch('displaySelectedItemObjectData', {
                                groupTitle: newVal.title,
                                feature: newVal.feature
                            });
                        } else {
                            // find group by layerId to correctly handle groups that
                            // contain the same set of objects.
                            const groupIndex = this.getSelectionGroups.findIndex(
                                (group) => group.id === newVal.layerId
                            );

                            // Get editable property from group
                            const group = this.getSelectionGroups[groupIndex];
                            this.editable = group.editable;
                            this.remove = group.remove;

                            const itemIndex = group.objects.findIndex(
                                (item) => item.id === newVal.id
                            );

                            this.$store.dispatch('updateSelectionPointer', {
                                group: groupIndex,
                                index: itemIndex
                            });
                        }
                        this.$_zoomToFeature(newVal.feature);
                    }
                },
                immediate: true
            },
            selectedItemIndex: {
                handler: function (newVal) {
                    if (
                        this.active &&
                        newVal === -1 &&
                        this.selectedObject?.type !== 'api'
                    ) {
                        this.$store.dispatch('updateSelectionPointer', {
                            index: 0
                        });
                    }
                },
                immediate: true
            }
        },
        computed: {
            ...mapGetters([
                'isMobile',
                'getSelectionGroups',
                'getSelectionObjectsSelectedItemObjectlistIndex',
                'getSelectionObjects',
                'getSelectionObjectsSelectedItem',
                'getSelectionObjectsSelectedGroup',
                'getObjectDataSelectedItem',
                'getObjectDataSelectedItemIndex',
                'getObjectDataSelectedGroupIndex'
            ]),
            ...mapState(['selectedItemIndex']),
            selectedIndex() {
                if (this.selectedObject?.type === 'api') {
                    return this.getObjectDataSelectedItemIndex;
                } else {
                    return this.getSelectionObjectsSelectedItemObjectlistIndex;
                }
            },
            panelTitle() {
                if (this.selectedObject?.type === 'api') {
                    return this.selectedObject.title;
                } else {
                    const selectedGroup = this.getSelectionObjectsSelectedGroup;
                    return selectedGroup.title;
                }
            },
            currentObject() {
                if (this.selectedObject?.type === 'api') {
                    return this.getObjectDataSelectedItem(
                        this.getObjectDataSelectedGroupIndex,
                        this.getObjectDataSelectedItemIndex
                    );
                } else {
                    return this.getSelectionObjectsSelectedItem;
                }
            },
            isFeedBack() {
                return this.selectedObject?.type === 'feedback';
            }
        },
        methods: {
            $_onIndexChanged(objectIndex) {
                if (this.currentObject?.type === 'api') {
                    this.$store.commit('SET_SELECTION_OBJECT_DATA_POINTER', {
                        group: this.getObjectDataSelectedGroupIndex,
                        index: objectIndex
                    });
                    if (this.currentObject.rowClick) {
                        this.$store.dispatch(this.currentObject.rowClick.action, {
                            item: this.currentObject.item
                        });
                    }
                    this.$emit('open', this.currentObject);
                } else {
                    const newItem = this.getSelectionObjects[objectIndex];
                    const group = this.getSelectionGroups.findIndex(
                        (item) => item.id === newItem.layerId
                    );
                    const index = this.getSelectionGroups[group].objects.findIndex(
                        (item) => item.id === newItem.id
                    );
                    this.$store.commit('SET_SELECTION_POINTER', { group, index });
                    this.$emit('open', this.currentObject);
                }
            },
            $_hide() {
                this.$emit('hide');
            },
            $_editFeedback() {
                const feature = this.currentObject.feature;
                const layer = this.currentObject.layer;
                const panelName =
                    layer.extended.contentTypeId === CONTENT_TYPE_ID.FEEDBACK
                        ? 'feedbackEdit'
                        : 'measureEdit';
                this.$store.dispatch('togglePanel', {
                    panelName,
                    visible: true,
                    data: { layer, feature }
                });
            },
            $_deleteFeedbackModel() {
                const feature = this.currentObject.feature;
                const layer = this.currentObject.layer;
                const panelName =
                    layer.extended.contentTypeId === CONTENT_TYPE_ID.FEEDBACK
                        ? 'feedbackEdit'
                        : 'measureEdit';
                this.$store.dispatch('togglePanel', {
                    panelName,
                    visible: true,
                    data: { layer, features: [feature] },
                    mode: 'delete'
                });
            },
            $_zoomToFeature(feature) {
                if (feature && (!isSmallDevice() || this.mode !== 'panel')) {
                    this.$store.dispatch('zoomToFeature', {
                        mapName: 'main',
                        feature: [feature],
                        usePanelMargin: this.mode === 'panel'
                    });
                }
            },
            $_printFeedback() {
                const printJob = {
                    title: this.panelTitle,
                    selectedObject: this.selectedObject
                };
                this.$store.commit('SET_FEEDBACK_PRINT_JOB', printJob);
                this.$store.dispatch('trackUserEvent', { name: 'feedbackPrint' });
                this.$router.push({ name: 'ngFeedbackPrint' });
            }
        }
    };
</script>

<style lang="scss" scoped>
.object-content {
    overflow: auto;
    flex: 1;
}

.panel-content {
    display: flex;
    flex-direction: column;
}

.object-detail-buttons {
    margin-top: 10px;
    display: flex;
    justify-content: flex-end;
}

.object-detail-buttons > div {
    margin-right: 3px;
}
</style>
